import React, { useCallback } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'

import {
  AddCarPage,
  AddCustomerPage,
  AddDealPage,
  AddDriverPage,
  AddProposalPage,
  AddReportPage,
  AuthWrapper,
  Cars,
  Deals,
  Drivers,
  EditCarPage,
  EditCustomerPage,
  EditDealPage,
  EditDriverPage,
  EditProposalPage,
  EditReportPage,
  HomePage,
  Invoices,
  MainInformation,
  NotFoundPage,
  Proposals,
  Reports,
  SignInPage,
} from '@/pages/index'

import { AppLayoutWithSidebar } from '@/components'
import { useCanWrite } from '@/hooks'
import {
  addCarPath,
  addCustomerPath,
  addDealPath,
  addDriverPath,
  addProposalPath,
  addReportPath,
  carsPath,
  dealsPath,
  driversPath,
  editCarPath,
  editCustomerPath,
  editDealPath,
  editDriverPath,
  editProposalPath,
  editReportPath,
  homePath,
  invoicesPath,
  mainInformationPath,
  notFoundPath,
  proposalPath,
  reportsPath,
  searchedPath,
  signInPath,
} from '@/utils/index'

const AppRoutes: React.FC = () => {
  const canWrite = useCanWrite()

  const onError = useCallback((e: Error) => {
    console.log({ appError: e })
  }, [])

  return (
    <ErrorBoundary onError={onError} fallback={<div />}>
      <Routes>
        <Route path="/" element={<Navigate to={homePath} />} />
        <Route path={signInPath} element={<SignInPage />} />
        <Route path={notFoundPath} element={<NotFoundPage />} />
        <Route element={<AuthWrapper />}>
          <Route path={homePath} element={<HomePage />} />
          <Route
            path={addCarPath}
            element={canWrite ? <AddCarPage /> : <NotFoundPage />}
          />
          <Route path={editCarPath} element={<EditCarPage />} />
          <Route
            path={addDealPath}
            element={canWrite ? <AddDealPage /> : <NotFoundPage />}
          />
          <Route path={editDealPath} element={<EditDealPage />} />
          <Route
            path={addProposalPath}
            element={canWrite ? <AddProposalPage /> : <NotFoundPage />}
          />
          <Route path={editProposalPath} element={<EditProposalPage />} />
          <Route
            path={addCustomerPath}
            element={canWrite ? <AddCustomerPage /> : <NotFoundPage />}
          />
          <Route path={editCustomerPath} element={<EditCustomerPage />} />
          <Route
            path={addDriverPath}
            element={canWrite ? <AddDriverPage /> : <NotFoundPage />}
          />
          <Route path={editDriverPath} element={<EditDriverPage />} />
          <Route
            path={addReportPath}
            element={canWrite ? <AddReportPage /> : <NotFoundPage />}
          />
          <Route path={editReportPath} element={<EditReportPage />} />
          <Route
            path={searchedPath}
            element={
              <AppLayoutWithSidebar>
                <Outlet />
              </AppLayoutWithSidebar>
            }
          >
            <Route index element={<Navigate to="main" />} />
            <Route path={mainInformationPath} element={<MainInformation />} />
            <Route path={carsPath}>
              <Route index element={<Cars />} />
              <Route path=":id" element={<Cars />} />
            </Route>
            <Route path={dealsPath}>
              <Route index element={<Deals />} />
              <Route path=":id" element={<Deals />} />
            </Route>

            <Route path={reportsPath}>
              <Route index element={<Reports />} />
              <Route path=":id" element={<Reports />} />
            </Route>
            <Route path={driversPath}>
              <Route index element={<Drivers />} />
              <Route path=":id" element={<Drivers />} />
            </Route>
            <Route path={invoicesPath}>
              <Route index element={<Invoices />} />
              <Route path=":id" element={<Invoices />} />
            </Route>
            <Route path={proposalPath}>
              <Route index element={<Proposals />} />
              <Route path=":id" element={<Proposals />} />
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </ErrorBoundary>
  )
}

export default AppRoutes
